import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { autocompleteClasses, createFilterOptions } from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

const LISTBOX_PADDING = 8; // px

const KeySearchContext = React.createContext<string | undefined>(undefined);

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const keySearch = React.useContext(KeySearchContext) ?? '';

    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
    };

    if (dataSet.hasOwnProperty('group')) {
        return (
            <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
                {dataSet.group}
            </ListSubheader>
        );
    }

    const { key, ...optionProps } = dataSet[0];
    return (
        <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
            {`${dataSet[1][keySearch]}`}
        </Typography>
    );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData: React.ReactElement<unknown>[] = [];
    (children as React.ReactElement<unknown>[]).forEach(
        (
            item: React.ReactElement<unknown> & {
                children?: React.ReactElement<unknown>[];
            },
        ) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
        },
    );


    const itemCount = itemData.length;
    const itemSize = 48;

    const getChildSize = (child: React.ReactElement<unknown>) => {
        if (child.hasOwnProperty('group')) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index: any) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});


const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});


export default function AutocompleteElement({ options, keySearch, callBack }:
    {
        options: any[], keySearch: string, callBack: (data: string) => void

    }) {
    const theme = useTheme()
    const { t } = useTranslation()
    const [value, setValue] = React.useState<string | null>(null);
    const [inputValue, setInputValue] = React.useState<any>('');

    const filterOptions = createFilterOptions({
        matchFrom: 'start',
        trim: true,
        limit: 5,
        stringify: (data: any) => data.symbol
    })


    return (
        <KeySearchContext.Provider value={keySearch}>
            <Autocomplete
                autoSelect={inputValue ? true : false}
                blurOnSelect={true}
                autoHighlight={true}
                clearOnBlur={true}
                size="small"
                filterOptions={filterOptions}
                value={value}
                onChange={(_event: any, newValue: any | null) => {
                    setValue(newValue);
                    callBack && callBack(newValue?.[keySearch] ?? "")
                }}

                inputValue={inputValue}
                onInputChange={(_event, newInputValue: any) => {
                    setInputValue(newInputValue);
                }}
                sx={{ width: 200 }}
                disableListWrap
                options={options}
                renderInput={(params) => <TextField sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, backgroundColor: theme.palette.background.input }}  {...params} placeholder={t("copytrade.common.symbol")} />}
                renderOption={(props, option, state) =>
                    [props, option, state.index] as React.ReactNode
                }
                slots={{
                    popper: StyledPopper,
                }}
                slotProps={{
                    listbox: {
                        component: ListboxComponent
                    },
                }}
                getOptionLabel={(data) => data[keySearch]}
            />
        </KeySearchContext.Provider>

    );
}
